///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// NHC wind speed probability swath for one storm, draped on the globe
/// under the forecast cone: the nested probability bands for the chosen
/// threshold (34 / 50 / 64 kt) as translucent fills whose stacking darkens
/// toward the track, each band with a hairline outline in its own color so
/// the structure survives busy backgrounds (SST, cloud overlays). The
/// cone says where the center may go; this says where the winds may reach,
/// folding track, intensity and size error together.
///

import { DrapedPolygonGeometry } from "@/earthbrowser/entities/geometries/DrapedPolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { vec2 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type WindProbability } from "./HurricanesTable"
import { polygonsFor, probabilityColor, probabilityFill, type WindProbabilityThreshold } from "./windProbability"

/** Fills sit just under the cone's drape so the cone outline reads over them */
const cFillDropKm = 0.1
const cOutlineLiftKm = 0.12

export class StormWindProbabilityNode extends NodeBase {
	private readonly nodeContext: StovokorContext
	private readonly probability: WindProbability
	private readonly altitudeKm: number
	private fills: Array<{ node: SimpleNode; pct: number }> = []
	private outlines: Array<{ node: PolylineNode; pct: number }> = []
	private focused = false
	private threshold: WindProbabilityThreshold = 34

	constructor(context: StovokorContext, probability: WindProbability, altitudeKm: number) {
		super()
		this.nodeContext = context
		this.probability = probability
		this.altitudeKm = altitudeKm
		this.build()
	}

	public setThreshold(kt: WindProbabilityThreshold): void {
		if (this.threshold === kt) return
		this.threshold = kt
		this.clear()
		this.build()
	}

	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused
		for (const fill of this.fills) fill.node.color = probabilityFill(fill.pct, focused)
		for (const outline of this.outlines) outline.node.updateColor(this.outlineColor(outline.pct))
	}

	private outlineColor(pct: number) {
		const c = probabilityColor(pct)
		return rgba(c.r, c.g, c.b, this.focused ? 0.6 : 0.3)
	}

	private build(): void {
		for (const band of polygonsFor(this.probability, this.threshold)) {
			const ring = band.ring.map(([lon, lat]) => new vec2(lon, lat))
			try {
				const geometry = new DrapedPolygonGeometry(this.nodeContext, ring, this.altitudeKm - cFillDropKm)
				const node = new SimpleNode(this.nodeContext, geometry, probabilityFill(band.pct, this.focused), { polygonOffset: cSurfaceOverlayDepthBias })
				this.fills.push({ node, pct: band.pct })
				this.addNode(node)
			} catch {
				// Degenerate ring after simplification: skip the fill
			}
			const positions = band.ring.map(([lon, lat]) => new geographic(lon * trig.rad, lat * trig.rad, this.altitudeKm + cOutlineLiftKm).cartesian)
			if (positions.length >= 3) {
				const node = new PolylineNode(this.nodeContext, {
					positions,
					width: 1,
					color: this.outlineColor(band.pct),
					loop: true,
					depthWrite: false,
					polygonOffset: cSurfaceOverlayDepthBias,
				})
				this.outlines.push({ node, pct: band.pct })
				this.addNode(node)
			}
		}
	}

	private clear(): void {
		for (const fill of this.fills) {
			this.removeNode(fill.node)
			fill.node.destroy()
		}
		for (const outline of this.outlines) {
			this.removeNode(outline.node)
			outline.node.destroy()
		}
		this.fills = []
		this.outlines = []
	}
}
